import { Link } from "react-router-dom";
import useDepartmentDoctors from "../hooks/useDepartmentDoctors";
import DoctorCard from "./DoctorCard";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import SEO from "../pages/SEO"
import { useAppointment } from "../context/AppointmentContext"
import {
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
  Users,
  Award,
  Clock,
  Heart
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "../ui/accordion";

export default function RadiologyPage() {
  const { open: openAppointment } = useAppointment();
  const doctors = useDepartmentDoctors("Radiology");


  const subspecialties = [
    {
      title: "Diagnostic Radiology",
      description: "X-ray, ultrasound, CT & MRI for all conditions.",
      image: "/DiagnosticRadiology.jpg"
    },
    {
      title: "Interventional Radiology",
      description: "Minimally invasive image-guided procedures.",
      image: "/InterventionalRadiology.jpg"
    },
    {
      title: "Neuroradiology",
      description: "Brain, spine & nerve imaging.",
      image: "/Neuroradiology.jpg"
    },
    {
      title: "Musculoskeletal Imaging",
      description: "Bones, joints, ligaments & sports injury scans.",
      image: "/MusculoskeletalImaging.jpg"
    },
    {
      title: "Women's Imaging",
      description: "Mammography, breast ultrasound & pelvic imaging.",
      image: "/Women'sImaging.jpg"
    },
    {
      title: "Vascular Imaging",
      description: "Doppler studies for arteries & veins.",
      image: "/VascularImaging.jpg"
    }
  ];

  const treatments = [
    "MRI Scan (1.5T / 3T) - Brain, spine, joints & soft tissue studies",
    "CT Scan (128 Slice / High Resolution) - Trauma, chest, abdomen & angiography",
    "Ultrasound & Doppler - Pregnancy, abdomen, pelvis, thyroid & vascular",
    "Digital X-Ray - Chest, limbs, spine, skull & emergency imaging",
    "Mammography - Breast cancer screening",
    "Fluoroscopy - GI studies & contrast imaging",
    "FNAC & Biopsies",
    "Image-guided aspirations, catheter placements, angiography & embolization"
  ];

  const whyChoose = [
    {
      icon: Award,
      title: "Latest Imaging Technology",
      description: "Latest imaging technology (CT, MRI, digital X-ray, ultrasound)"
    },
    {
      icon: Users,
      title: "Expert Radiologists",
      description: "Experienced radiologists with subspecialty expertise"
    },
    {
      icon: Clock,
      title: "Quick Reporting",
      description: "Quick reporting with high diagnostic accuracy"
    },
    {
      icon: Heart,
      title: "Safe Equipment",
      description: "Safe, low-radiation equipment & patient-friendly scanning"
    },
    {
      icon: CheckCircle2,
      title: "24x7 Emergency Support",
      description: "24x7 emergency imaging support for urgent cases"
    }
  ];

  const articles = [
    {
      title: "Why Accurate Imaging is Critical for Early Diagnosis",
      excerpt:
        "Learn how advanced imaging technology helps detect diseases early and improves treatment outcomes."
    },
    {
      title: "MRI vs CT Scan - What's the Difference?",
      excerpt:
        "Understand the key differences between MRI and CT scans and when each is most appropriate."
    },
    {
      title: "How Doppler Ultrasound Helps Identify Vascular Problems",
      excerpt:
        "Discover how Doppler ultrasound can detect blood flow issues and vascular diseases."
    },
    {
      title: "Importance of Mammography in Women Over 40",
      excerpt:
        "Learn why regular mammography screening is crucial for early breast cancer detection."
    }
  ];

  const faqs = [
    {
      question: "Is MRI safe?",
      answer:
        "Yes, MRI uses magnetic fields and is radiation-free. However, people with certain implants should inform their doctor."
    },
    {
      question: "Do you offer 24x7 imaging services?",
      answer:
        "Yes, CT, X-ray & ultrasound are available round-the-clock for emergencies."
    },
    {
      question: "Are interventional radiology procedures painful?",
      answer:
        "Most procedures are minimally invasive and done under local anesthesia with minimal discomfort."
    }
  ];
  const schema = [
  {
    "@context": "https://schema.org",
    "@type": "MedicalClinic",
    "name": "MAMS Hospitals Radiology Department",
    "url": "https://mamshospitals.com/radiology",
    "logo": "https://mamshospitals.com/MAMS-Logo.png",
    "image": "https://mamshospitals.com/Radiology.jpg",
    "description": "MAMS Hospitals provides advanced radiology and imaging services in Hyderabad including MRI scan, CT scan, ultrasound, Doppler studies, mammography, interventional radiology, and emergency imaging.",
    "medicalSpecialty": "Radiology",
    "telephone": "+91-7399999981",
    "priceRange": "$$",

    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Bachupally",
      "addressLocality": "Hyderabad",
      "addressRegion": "Telangana",
      "postalCode": "500090",
      "addressCountry": "IN"
    },

    "availableService": [
      {
        "@type": "MedicalProcedure",
        "name": "MRI Scan"
      },
      {
        "@type": "MedicalProcedure",
        "name": "CT Scan"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Ultrasound & Doppler"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Mammography"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Interventional Radiology"
      }
    ],

    "sameAs": [
      "https://www.facebook.com/MAMSHospitals",
      "https://www.instagram.com/mams_hospitals"
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Do you provide MRI and CT scan services?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, MAMS Hospitals offers advanced MRI, CT scan, digital X-ray, ultrasound, and Doppler imaging services."
        }
      },
      {
        "@type": "Question",
        "name": "Are radiology services available 24x7?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, emergency imaging including CT scan, X-ray, and ultrasound services are available 24x7."
        }
      },
      {
        "@type": "Question",
        "name": "Do you provide interventional radiology procedures?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, MAMS Hospitals provides image-guided minimally invasive interventional radiology procedures."
        }
      }
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Radiology Department - MAMS Hospitals",
    "url": "https://mamshospitals.com/radiology",
    "description": "Best radiology hospital in Hyderabad offering MRI scan, CT scan, ultrasound, Doppler, mammography, interventional radiology, and emergency imaging services.",
    "keywords": [
      "Radiology Hospital Hyderabad",
      "MRI Scan Hyderabad",
      "CT Scan Hyderabad",
      "Ultrasound Scan Hyderabad",
      "Best Radiologist Hyderabad",
      "Mammography Hyderabad",
      "Radiology Center Bachupally"
    ]
  }
];

  return (
    <>
    <SEO
  title="Best Radiology & Imaging Center in Hyderabad | MRI & CT Scan | MAMS Hospitals"
  description="MAMS Hospitals offers advanced radiology and imaging services in Hyderabad including MRI scan, CT scan, ultrasound, Doppler, mammography, interventional radiology, and emergency imaging."
  keywords="radiology hospital Hyderabad,
MRI scan Hyderabad,
CT scan Hyderabad,
ultrasound scan Hyderabad,
best radiologist Hyderabad,
mammography Hyderabad,
doppler scan Hyderabad,
interventional radiology Hyderabad,
digital xray Hyderabad,
diagnostic imaging Hyderabad,
emergency imaging Hyderabad,
radiology center Bachupally,
MAMS Hospitals Radiology"
  schema={schema}
/>
    <div className="flex flex-col min-h-screen">

      {/* HERO */}
      <section className="relative bg-gradient-to-br from-teal-50 via-white to-blue-50 py-12 md:py-24">
        <div className="container mx-auto px-0 max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">

            <div>
              <div className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                Best Radiology & Imaging Sciences in Hyderabad
              </div>

              <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
                Clear Imaging, Accurate Diagnosis
              </h1>

              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                MAMS Hospitals is home to one of the Best Radiology & Imaging Sciences Departments in Hyderabad, offering advanced diagnostic imaging services with state-of-the-art technology.
              </p>

             <div className="flex flex-col sm:flex-row gap-4">

                <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8" size="lg" onClick={openAppointment}>
                    Book Appointment
                  </Button>

                <Link to="tel:+91-7399999981">
                  <Button
                    className="border-teal-600 text-teal-600 hover:bg-teal-50 rounded-full px-8"
                    variant="outline"
                    size="lg"
                  >
                    <Phone className="mr-2 h-5 w-5" />
                    Call Us
                  </Button>
                </Link>

              </div>
            </div>

            <div className="relative">
              <div className="absolute -top-4 -right-4 bg-teal-600 text-white p-6 rounded-2xl shadow-xl">
                <div className="text-3xl font-bold">24/7</div>
                <div className="text-sm">Emergency Imaging</div>
              </div>

              <img
                src="/Radiology.jpg"
                alt="Radiology at MAMS Hospital"
                className="rounded-2xl shadow-2xl w-full"
              canonicalUrl="https://mamshospitals.com/departments/radiology"
      />
            </div>

          </div>
        </div>
      </section>

      {/* SUBSPECIALTIES */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto max-w-6xl px-4 text-center">

          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Sub-Specialties in Radiology & Imaging Sciences
          </h2>

          <p className="text-gray-600 max-w-3xl mx-auto mb-12">
            Complete imaging expertise across all radiology domains
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {subspecialties.map((item, i) => (
              <Card key={i} className="hover:shadow-xl border-gray-200 transition">
                <CardContent className="p-0">
                  <div className="h-48 overflow-hidden rounded-t-lg">
                    <img
                      src={item.image}
                      className="object-cover w-full h-full"
                      alt={item.title}
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold">{item.title}</h3>
                    <p className="text-gray-600 mt-2">{item.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

        </div>
      </section>

      {/* TREATMENTS */}
      <section className="bg-gradient-to-br from-gray-50 to-blue-50 py-16 md:py-20">
        <div className="container mx-auto px-0 max-w-6xl text-center">

          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Diagnostic Services & Procedures
          </h2>

          <p className="text-gray-600 max-w-3xl mx-auto mb-12">
            Advanced imaging performed with precision & safety
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            {treatments.map((t, i) => (
              <div key={i} className="flex gap-3 bg-white p-4 rounded-lg shadow">
                <CheckCircle2 className="h-6 w-6 text-teal-600 mt-1" />
                <span className="text-gray-700">{t}</span>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto max-w-6xl px-4">

          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Why Choose MAMS Hospitals for Radiology
          </h2>

          <p className="text-gray-600 text-center max-w-3xl mx-auto mb-12">
            Trusted imaging excellence with world-class equipment
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChoose.map((item, i) => (
              <Card key={i} className="hover:shadow-lg border-gray-200 transition">
                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-teal-100 flex items-center justify-center rounded-full mb-4">
                    <item.icon className="h-7 w-7 text-teal-600" />
                  </div>
                  <h3 className="font-bold text-xl">{item.title}</h3>
                  <p className="text-gray-600 mt-2">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

        </div>
      </section>

      {/* SPECIALIST */}
                {doctors.length > 0 && (
          <section className="py-16 px-0 bg-apple-gray-50">
            <div className="container mx-auto max-w-6xl">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-semibold text-apple-gray-500 mb-4">Meet Our Specialists</h2>
              </div>
              <div className="relative">
                <button
                  onClick={() => { const el = document.getElementById('doc-scroll'); if(el) el.scrollBy({ left: -340, behavior: 'smooth' }); }}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 hover:bg-gray-100"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
                <div id="doc-scroll" className="flex gap-6 overflow-x-auto scroll-smooth px-10 scrollbar-hide pb-4">
                  {doctors.map(doctor => (
                    <div key={doctor.id} className="w-[280px] sm:w-[300px] flex-shrink-0">
                      <DoctorCard doctor={{
                        name: doctor.title.rendered,
                        slug: doctor.slug,
                        imageUrl: doctor._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "",
                        specialty: doctor._embedded?.["wp:term"]?.[0]?.[0]?.name || "",
                        designation: doctor.acf?.designation || "",
                        qualification: doctor.acf?.qualification || "",
                        experience: doctor.acf?.experience || "",
                      }} />
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => { const el = document.getElementById('doc-scroll'); if(el) el.scrollBy({ left: 340, behavior: 'smooth' }); }}
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 hover:bg-gray-100"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>
              <div className="text-center mt-8">
                <Link to="/doctors">
                  <button className="px-6 py-3 rounded-full border text-sm font-medium inline-flex items-center gap-2">
                    View All Doctors
                  </button>
                </Link>
              </div>
            </div>
          </section>
          )}

      {/* ARTICLES */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto max-w-6xl px-4 text-center">

          <h2 className="text-3xl font-bold mb-4">Recent Articles</h2>
          <p className="text-gray-600 mb-12">Stay educated about radio-diagnosis</p>

          <div className="grid md:grid-cols-2 gap-6">
            {articles.map((a, i) => (
              <Card key={i} className="hover:shadow-xl border-gray-200 transition cursor-pointer">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold">{a.title}</h3>
                  <p className="text-gray-600 mt-2">{a.excerpt}</p>
                </CardContent>
              </Card>
            ))}
          </div>

        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto max-w-4xl px-4">

          <h2 className="text-3xl font-bold text-center mb-4">Frequently Asked Questions</h2>
          <p className="text-gray-600 text-center mb-12">
            Helpful answers about radiology & imaging
          </p>

          <Accordion type="single" collapsible>
            {faqs.map((f, i) => (
              <AccordionItem value={`faq-${i}`} key={i}>
                <AccordionTrigger>{f.question}</AccordionTrigger>
                <AccordionContent>{f.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

        </div>
      </section>

      {/* Contact & Appointment Section */}
      <section className="py-16 px-0 bg-apple-teal text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">Book an Appointment</h2>
            <p className="text-white/90 max-w-2xl mx-auto">
              Your heart health is our top priority. Book an appointment today with Hyderabad's trusted experts at MAMS Hospitals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Phone className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Call Us</h3>
                <a href="tel:+917399999981" className="text-white/90 hover:text-white">
                  +91-7399999981
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Mail className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Email Us</h3>
                <a href="mailto: info@mamshospitals.com" className="text-white/90 hover:text-white">
                   info@mamshospitals.com
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <MapPin className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Visit Us</h3>
                <p className="text-white/90">Bachupally, Miyapur</p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            {/* <Link href="/book-appointment">
              <Button size="lg" variant="secondary" className="rounded-full">
                <Calendar className="mr-2 h-5 w-5" />
                Schedule Your Appointment
              </Button>
            </Link> */}
          </div>
        </div>
      </section>

      {/* Emergency Banner */}
      <section className="py-8 px-0 bg-red-600 text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Clock className="h-8 w-8" />
              <div>
                <h3 className="text-xl font-bold">24/7 Emergency Care</h3>
                <p className="text-white/90">Fully equipped emergency department</p>
              </div>
            </div>
            <a href="tel:+917399999981">
              <Button size="lg" variant="secondary" className="w-full sm:w-auto rounded-full bg-white text-red-600 hover:bg-gray-100">
                <Phone className="mr-2 h-5 w-5" />
                Call Emergency
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
    </>
  );
}
